import { TRTCConnection, TRTCState } from './types';
import { VOIP_CONFIG } from 'config/voip';
import { State } from 'utils/state';
import { IRtcClient } from './types';
import { EConnectionEvents, ERTCEvents, EWebsocketClientEvents } from './events';
import { ISignalClient, TRawConnection } from './signal/types';
import { SignalerClient } from './signal';
import { myCameraStream } from 'state/video';
import { recordSocketGaveUp, recordSocketReconnectAttempt } from 'state/session/syncTelemetry';
import {
  recordVoipIceRestart,
  recordVoipIceRestartGiveUp,
  recordVoipPeerWrittenOff,
  recordVoipTransportRebuild,
} from 'state/session/voipQuality';
import log from 'utils/log';
import { EventEmitter, Unsubscribe } from 'utils/events';
import { SpeakerDetector } from 'utils/ui/speakerDetector';
import { VoipMediaState } from 'utils/ui/media';
import { filterUsableIceServers, getIceServersExpiry } from 'utils/ui/iceServers';

const defaultRTCState: TRTCState = {
  me: {
    connectionId: '',
  },
  remoteConnections: {},
};

/**
 * Sink for the Session Flight Recorder (AVS-4430).
 *
 * MODULE scope, not per-instance, for the same reason `setSignalFrameSink` is: `RTCClient.getInstance`
 * builds a NEW client after `destroy()` and `utils/ui/rtc.ts:26` reassigns its module binding on
 * takeover, so an instance-level hook goes blind exactly during the outage being recorded. Nothing
 * here may be keyed on `window.voipRtcConnection` either — that global is assigned only AFTER a
 * successful connect (`utils/ui/rtc.ts:713`), so anything reading it misses every connect FAILURE.
 *
 * A SINK, never a listener: `utils/events.ts:239` gives a listener's return value the
 * prevent-execution protocol, and `utils/ui/media.ts:144` aborts `getUserMedia` on a falsy one — a
 * lane wired as a listener would disable the camera it exists to observe. {@link reportRtc}
 * therefore returns `void` and discards whatever the sink returns.
 *
 * `payload` carries LIVE objects (an `RTCIceCandidate`, an `RTCSessionDescriptionInit`, a
 * `MediaStream`). This module deliberately applies no privacy policy to them: SDP and ICE
 * candidates carry IP addresses and the tiering that decides which of those survive lives with the
 * recorder (`telemetry/rtcStats.ts`), so this file keeps ZERO telemetry imports and stays absent
 * from the recorder's build gate.
 */
export type TRtcEventSink = (type: string, payload: Record<string, unknown>) => void;

let rtcEventSink: TRtcEventSink | null = null;

/** Register the recorder's RTC sink. `null` restores the no-op. Idempotent by assignment. */
export function setRtcEventSink(sink: TRtcEventSink | null): void {
  rtcEventSink = sink;
}

/**
 * Hand one observation to the sink, isolated.
 *
 * The sink is registered from outside and arbitrary: a throwing one must never fail a WebRTC call.
 * `console.error` rather than `log.*` matches `utils/events.ts:165-169` — several suites hand-mock
 * `utils/log` with a partial object, and an isolation path that itself throws is not an isolation
 * path. The try block holds only the sink call.
 */
function reportRtc(type: string, payload: Record<string, unknown>): void {
  if (!rtcEventSink) {
    return;
  }

  try {
    rtcEventSink(type, payload);
  } catch (error) {
    console.error('[VOIP] RTC recorder sink threw and was isolated', error);
  }
}

/** Which negotiation pass an RTCClient instance has run. See `RTCClient.lastNegotiationPass`. */
export type TNegotiationPass = 'none' | 'publish' | 'receive-only';

export class RTCClient extends EventEmitter implements IRtcClient {
  private static instance: RTCClient | null = null;

  private wsEndpoint: string;

  private signalerClient!: ISignalClient;

  private iceServers: RTCIceServer[] = [];

  // AVS-4391: when the cached TURN credentials die, so that createPeerConnection can say so
  // instead of silently building a peer that will gather no relay candidates. Null = unknown.
  private iceServersExpireAt: number | null = null;
  /** One report and one toast per client for a refusal that belongs to the browser, not the peer. */
  private peerConnectionUnavailableReported = false;

  private published: boolean;

  // AVS-4302: true once this client has run a receive-only negotiation pass (a standby).
  // `ConnectionAdded` reads it so peers that join AFTER negotiateAsReceiver() also negotiate
  // media-free — without it, a late-joining peer would take the default (publishing) path and
  // a standby holding a camera-grab-race stream could leak a second dealer stream. Reset to
  // false by publish(), so a standby promoted to master on the same instance shares its media.
  private receiveOnly: boolean = false;

  // AVS-4363 (T7b): which negotiation pass this instance has run. Written ONLY by publish()
  // and negotiateAsReceiver(), cleared by destroy() — never by the media-policy flags above.
  private negotiationPass: TNegotiationPass = 'none';

  private pendingCandidates: Record<string, RTCIceCandidate[]> = {};

  // AVS-4302 perfect-negotiation glare resolution (keyed by peer connectionId).
  // makingOffer: true while our own createOffer/setLocalDescription is in flight, so a colliding
  // inbound offer is detected even before signalingState flips to have-local-offer.
  private makingOffer: Record<string, boolean> = {};

  // ignoringOffer: true after we (the impolite peer) dropped a colliding offer; gates ICE
  // candidate-error swallowing to that discarded offer ONLY. Cleared the moment the surviving
  // negotiation reaches stable (an accepted offer, or our own kept offer getting answered).
  private ignoringOffer: Record<string, boolean> = {};

  // AVS-4302 perfect-negotiation recovery (keyed by peer connectionId).
  // renegotiationTimers: per-peer "awaiting answer" timer, armed when we send an offer and cleared
  // when the pair reaches stable (onsignalingstatechange). On expiry the answer never came, so we
  // RE-OFFER (forceReoffer) — never roll back, so a slow/surviving offer is never corrupted.
  private renegotiationTimers: Record<string, ReturnType<typeof setTimeout>> = {};

  // renegotiationRetries: bounds forceReoffer so a permanently unanswerable peer cannot loop forever.
  private renegotiationRetries: Record<string, number> = {};

  // iceGraceTimers: a single grace window per peer for a transient ICE 'disconnected' before we
  // escalate to restartIce. iceRestartCounts bounds the restarts so a dead peer cannot churn.
  private iceGraceTimers: Record<string, ReturnType<typeof setTimeout>> = {};

  private iceRestartCounts: Record<string, number> = {};

  // AVS-4430: latches the ICE-restart give-up so the recorder hears about an exhausted budget
  // EXACTLY ONCE per exhaustion. maybeRestartIce is re-entered on every subsequent ICE 'failed'
  // event for the same dead peer, and an unlatched event would bury the one moment that matters
  // under a repeating line. Cleared wherever iceRestartCounts is — a returned budget is a fresh
  // ladder, and a second genuine exhaustion is a second genuine event.
  private iceGiveUpReported: Record<string, boolean> = {};

  // AVS-4311 follow-up: bounds rebuildPeerTransport. A DTLS transport failure (connectionState
  // 'failed' while ICE is NOT 'failed') is fatal to the RTCPeerConnection — restartIce() cannot
  // revive it — so recovery replaces the pc entirely. The cap stops a permanently dead peer from
  // rebuild-looping.
  private transportRebuilds: Record<string, number> = {};

  // AVS-4441: how many times the silent-transport watchdog has escalated this peer. Deliberately
  // NOT refilled by handleIceConnectionStateChange alongside the three budgets above: an ICE
  // restart always flaps through 'connected' on its way to carrying nothing again, so refilling
  // there would let a permanently silent peer restart ICE every window for the whole call. Only a
  // fresh transport (rebuildPeerTransport), a departure (removePeer) or a session reset refills it.
  private silentTransportEscalations: Record<string, number> = {};

  // AVS-4302 takeover reliability: per-peer telemetry deadline armed by every negotiation pass.
  // If the pair has not reached connectionState 'connected' by then, ONE structured warn names
  // the peer and its signaling/ICE/stream state — the field failures this covers are rare and
  // Safari-skewed, and undiagnosable without a snapshot of the stuck peer.
  private convergenceTimers: Record<string, ReturnType<typeof setTimeout>> = {};

  private readonly convergenceReportMs = 8000;

  // AVS-4302 takeover reliability: an offer can legitimately beat its ConnectionAdded (the Add
  // burst is async and unordered vs peer offers), so such peers are admitted on demand
  // (metaPending). The cap stops a spoofed-offer flood from creating unbounded pcs.
  private readonly maxMetaPendingPeers = 8;

  private readonly renegotiationTimeoutMs = 5000;

  // AVS-4302 takeover reliability: deadline for the WAITING (non-initiator) side of a pair that
  // never negotiated. 2× the offerer's timeout so the initiator's own recovery gets the first
  // shot before the waiting side risks a (glare-safe) collision.
  private readonly waitingOfferTimeoutMs = 10000;

  private readonly maxRenegotiationRetries = 4;

  private readonly iceDisconnectGraceMs = 5000;

  private readonly maxIceRestarts = 3;

  private readonly maxTransportRebuilds = 2;

  // AVS-4441: silent-transport windows a peer gets before it is written off. Two ICE restarts is
  // the same shape of budget maybeRestartIce spends, and at ~20 s per window the peer is written
  // off around a minute after the media stopped — fast enough that the tile is dropped rather than
  // frozen, slow enough that a long stall in a recovering network is not mistaken for a dead one.
  private readonly maxSilentTransportEscalations = 2;

  // AVS-4392: candidates rejected by the current transport are retained for retry, so the queue
  // needs a ceiling — a candidate that can never apply would otherwise accumulate for the peer's
  // whole lifetime. Drops oldest-first (cf. maxMetaPendingPeers, the same bounded-resource idea).
  private readonly maxPendingCandidates = 50;

  public store: State<TRTCState>;

  public sessionId?: string;

  private listeners: Unsubscribe[] = [];

  private speakerDetector: SpeakerDetector;

  private registerStreamRetryCount: number = 0;

  private readonly maxRegisterStreamRetries: number = 2;

  private constructor(iceServers: RTCIceServer[], wsEndpoint: string) {
    super();
    // AVS-4391: the caller has just fetched this list, so now is its fetch time.
    this.updateIceServers(iceServers);
    this.wsEndpoint = wsEndpoint;
    this.store = new State<TRTCState>(defaultRTCState);
    this.published = false;
    this.speakerDetector = new SpeakerDetector({
      threshold: 0.03,
      interval: 200,
      speakingTimeout: 2000,
    });
  }

  // Singleton instance accessor with lazy initialization
  public static getInstance(iceServers: RTCIceServer[], wsEndpoint: string): RTCClient {
    if (!RTCClient.instance || wsEndpoint !== RTCClient.instance.wsEndpoint) {
      RTCClient.instance = new RTCClient(iceServers, wsEndpoint);
    } else {
      // AVS-4391: the reuse path used to DISCARD the freshly fetched credential list, pinning
      // the surviving instance to TURN credentials that expire ~5 min after ITS construction.
      RTCClient.instance.updateIceServers(iceServers);
    }

    return RTCClient.instance;
  }

  /**
   * The live singleton, or `null` when none is constructed.
   *
   * AVS-4430: the recorder's stats sampler reads this on EVERY tick and never caches the result.
   * `getInstance` builds a new client after `destroy()` (which nulls this), and `utils/ui/rtc.ts:26`
   * reassigns its own module binding on takeover — a cached instance would sample a dead client for
   * the rest of the call. Unlike `getInstance` this never constructs, so an observer can ask
   * "is there a client?" without becoming the reason there is one.
   */
  public static current(): RTCClient | null {
    return RTCClient.instance;
  }

  /**
   * AVS-4391: replace the cached ICE server list so every FUTURE RTCPeerConnection — a late
   * joiner, a rejoin, an AVS-4311 transport rebuild — gathers with live TURN credentials
   * (KVS credentials expire after Ttl, default 300s; rtc.ts refreshes them on a timer).
   * Existing pcs keep their config: ICE servers apply at construction time only.
   *
   * `fetchedAtMs` anchors the expiry used for the staleness warning below. It defaults to now,
   * which is right for every caller (each one has just fetched the list); rtc.ts passes its own
   * pre-dispatch stamp so a slow response cannot make the credentials look fresher than they are.
   *
   * AVS-4418: the list is sanitised HERE, the one place a fetched list enters the client, so every
   * pc, the expiry below, the `pc-created` telemetry and the refresh scheduler (via
   * `iceServerList`) all see the same entries. An entry the RTCPeerConnection constructor refuses
   * — no urls (the `{}` an older lambda serialises when KVS returns nothing), a turn url without
   * credentials — used to fail EVERY peer construction for this client. Only counts are logged: a
   * dropped entry may still carry a credential, and the rtc lane applies no redaction.
   *
   * `iceServers` is `unknown` because that is what it really is: every list reaching here came
   * from `sendApiRequest`, which casts a parsed body to its type parameter without checking it.
   * Typing the parameter `RTCIceServer[]` would only restate the server's claim and make the
   * guard below look unreachable to a reader.
   */
  public updateIceServers(iceServers: unknown, fetchedAtMs: number = Date.now()): void {
    const usable = filterUsableIceServers(iceServers);
    if (!Array.isArray(iceServers)) {
      // Unreachable from our lambda, but the refresh scheduler calls this outside its try and a
      // throw here would leave the loop dead for the rest of the call.
      log.warn('[VOIP] ICE server list is not an array; peers will be built without ICE servers', typeof iceServers);
    } else if (usable.length < iceServers.length) {
      log.warn(
        '[VOIP] Dropped malformed ICE server entries; peers will be built from the rest',
        iceServers.length - usable.length
      );
    }
    this.iceServers = usable;
    this.iceServersExpireAt = getIceServersExpiry(usable, fetchedAtMs);
  }

  /**
   * The list every future peer connection is built from — sanitised (AVS-4418). The refresh
   * scheduler in utils/ui/rtc.ts schedules off THIS, never the raw body it fetched, so the two can
   * never disagree about when the credentials die.
   */
  public get iceServerList(): RTCIceServer[] {
    return this.iceServers;
  }

  public async connect(): Promise<void> {
    try {
      this.signalerClient = new SignalerClient(this.wsEndpoint);
      const connection = await this.signalerClient.connect();
      this.setupListeners();
      this.store.setValues({
        me: {
          ...connection,
        },
      });
      this.emit('voip:connected', connection);
      log.log('[VOIP] Signaling Client Connected', connection, Date.now().toString().slice(-5));
      // Attempt to stream the user's camera and microphone. In most cases this will be too early and won't happen.
      this.registerOutgoingStream();
    } catch (error) {
      log.log(
        '[VOIP] Unable to connect to the stream. Destroying signaler client.',
        error,
        Date.now().toString().slice(-5)
      );
      if (this.signalerClient) this.signalerClient.disconnect();
      throw new Error('RTC Client Connection Failure', { cause: error });
    }
  }

  public registerOutgoingStream() {
    const stream = myCameraStream().stream;
    const me = this.store.get('me') ?? {};
    log.log('[VOIP] Registering Outgoing Stream', stream, me, Date.now().toString().slice(-5));
    if (stream) {
      this.store.setValues({
        me: {
          ...me,
          stream,
        },
      });
      this.shareMyStream();

      log.log('[VOIP] Your stream is published', Date.now().toString().slice(-5));
      // Reset retry count on successful registration
      this.registerStreamRetryCount = 0;
    } else {
      // No local stream available - user is in receive-only mode
      // This is a valid state (e.g., when camera/mic permissions are denied)
      log.log('[VOIP] No outgoing stream to register - receive-only mode', Date.now().toString().slice(-5));
      this.registerStreamRetryCount = 0;
    }
  }

  registerMyVideoStreamListeners() {
    const unListenTrackAdded = myCameraStream().on('track:added', (track: MediaStreamTrack) => {
      this.addMyTrackToAllPeers(track);
      // The unmute re-grab lands async — toggleMedia signaled before the track existed,
      // so this re-signal is what flips the peers back to audible (AVS-4278).
      this.signalMutedStateToAllPeers();
    });

    const unListenTrackRemoved = myCameraStream().on('track:stopped', (track: MediaStreamTrack) => {
      this.removeMyTrackFromAllPeers(track);
      this.signalMutedStateToAllPeers();
    });

    this.listeners = [unListenTrackAdded, unListenTrackRemoved];
  }

  public destroy(): void {
    const { me, remoteConnections } = this.store.data;

    log.log('[VOIP] Destryoing Stream', Date.now().toString().slice(-5), remoteConnections);

    // AVS-4430: the takeover boundary. `getInstance` builds a NEW client after this, so a recording
    // that does not mark the seam reads the two clients' peers as one confusing sequence.
    reportRtc('client-destroyed', {
      sessionId: this.sessionId,
      peers: Object.keys(remoteConnections),
      published: this.published,
      receiveOnly: this.receiveOnly,
      negotiationPass: this.negotiationPass,
      hadLocalStream: !!me.stream,
    });

    if (this.signalerClient) this.signalerClient.disconnect();

    // AVS-4302: cancel every pending recovery timer BEFORE closing pcs, and null the pc event
    // handlers so a close-induced state change cannot arm a post-teardown timer or re-offer.
    Object.values(this.renegotiationTimers).forEach((timer) => clearTimeout(timer));
    Object.values(this.iceGraceTimers).forEach((timer) => clearTimeout(timer));
    Object.values(this.convergenceTimers).forEach((timer) => clearTimeout(timer));

    Object.values(remoteConnections).forEach(({ peerConnection }) => {
      if (peerConnection) {
        peerConnection.onnegotiationneeded = null;
        peerConnection.onsignalingstatechange = null;
        peerConnection.oniceconnectionstatechange = null;
        peerConnection.onicecandidate = null;
        peerConnection.ontrack = null;
        peerConnection.onconnectionstatechange = null;
        peerConnection.close();
      }
    });

    this.store.setValues(defaultRTCState);

    // Drop the local-stream track listeners BEFORE releasing the camera, so the release's
    // `track:stopped` does not bounce back through `removeMyTrackFromAllPeers` during teardown.
    this.listeners.forEach((unListen) => unListen());
    this.listeners = [];

    // AVS-4302: release the local camera THROUGH its manager (which clears the reference and
    // notifies consumers) instead of a raw `track.stop()` that leaves `myCameraStream().stream`
    // pointing at a DEAD stream. That dead stream was reused after a reconnect/step-down (and in
    // the minimized pill) and rendered BLACK until a manual video cycle re-grabbed it — because
    // the re-grab guards (`!myCameraStream().stream`, `if (myCameraStream().stream) return`) saw a
    // non-null-but-dead stream and skipped re-acquisition. Clearing the reference makes the next
    // init() / takeover grab a fresh, live camera. Only when this client OWNS that stream (a
    // publisher registered it as `me.stream`): a receive-only standby never registered one — and a
    // gesture-acquired takeover camera (me.stream still unset) must survive the destroy untouched.
    const localCamera = myCameraStream();
    if (me.stream && me.stream === localCamera.stream) {
      localCamera.stopStream();
    } else {
      me.stream?.getTracks().forEach((track: MediaStreamTrack) => track.stop());
    }

    this.speakerDetector.stop();

    // Reset retry count on destroy
    this.registerStreamRetryCount = 0;

    // AVS-4302: make destroy() a real teardown so a takeover destroy→reconnect cycle
    // is leak-free and a genuine re-init is not short-circuited.
    // - Null the singleton: getInstance (L59) otherwise returns this dead instance for
    //   an unchanged wsEndpoint.
    // - Reset sessionId: rtc.ts's `voipRtcConnection?.sessionId === sessionId` early-return
    //   reads the OLD instance until the next getInstance reassigns it; clearing sessionId
    //   stops that check from matching and short-circuiting a real re-init.
    // - Reset published so a reconnected client re-publishes from scratch.
    this.published = false;
    this.receiveOnly = false;
    this.negotiationPass = 'none';
    this.sessionId = undefined;
    this.makingOffer = {};
    this.ignoringOffer = {};
    this.renegotiationTimers = {};
    this.renegotiationRetries = {};
    this.iceGraceTimers = {};
    this.iceRestartCounts = {};
    this.iceGiveUpReported = {};
    this.transportRebuilds = {};
    this.silentTransportEscalations = {};
    this.convergenceTimers = {};
    RTCClient.instance = null;
  }

  /**
   * Which negotiation pass (`publish()` / `negotiateAsReceiver()`) this instance last ran.
   * Lives on the instance so the trigger in `useVoipConnections` survives a React remount
   * against a still-live connection (hook refs reset on every mount and would re-offer to
   * every peer); `destroy()` resets it, so a genuine rebuild re-runs its pass. Deliberately
   * not derived from `published`/`receiveOnly` — those have a third writer
   * (`liftIncomingMediaSuppression`), and a derived guard would skip a promoted master's
   * `publish()`.
   */
  public get lastNegotiationPass(): TNegotiationPass {
    return this.negotiationPass;
  }

  public async publish(): Promise<void> {
    this.published = true;
    this.negotiationPass = 'publish';
    // A publisher shares media with every peer, including late-joiners (ConnectionAdded). Clear
    // the standby's receive-only flag so a standby promoted to master on the same instance is
    // not stuck negotiating media-free.
    this.receiveOnly = false;
    // Re-enable incoming tracks a prior receive-only pass disabled (same-instance promotion).
    this.applyIncomingMediaPolicyToAll();
    const remoteConnections = this.store.get('remoteConnections');

    log.log('[VOIP] Publishing Stream', Date.now().toString().slice(-5), remoteConnections);

    Object.keys(remoteConnections).forEach((connectionId) => {
      this.prepareConnectionAndOffer(connectionId);
    });

    this.speakerDetector.start();
  }

  /**
   * AVS-4302: a standby must not PLAY incoming media (participants would be heard/seen in
   * every tab). While `receiveOnly` is set, every received track is disabled — RTP keeps
   * flowing so a takeover restores instantly, with no renegotiation. Bypassed under the
   * `loopbackVideo` debug flag, which exists to inspect live loopback streams.
   */
  private get suppressIncomingMedia(): boolean {
    return this.receiveOnly && !VOIP_CONFIG.loopbackVideo;
  }

  // Optional-call guard instead of `instanceof MediaStream`: policy must also cover
  // reconstructed/foreign stream objects that carry tracks without the prototype.
  private applyIncomingMediaPolicy(stream?: MediaStream): void {
    const enabled = !this.suppressIncomingMedia;
    stream?.getTracks?.().forEach((track) => {
      track.enabled = enabled;
    });
  }

  private applyIncomingMediaPolicyToAll(): void {
    const remoteConnections = this.store.get('remoteConnections') ?? {};
    Object.values(remoteConnections).forEach(({ stream }) => this.applyIncomingMediaPolicy(stream));
  }

  /**
   * AVS-4302: takeover pre-rebuild unsuppression. `performVoipTakeover` flips `isVoipStandby`
   * synchronously (local `voipMasters` write) but only destroys this client AFTER the network
   * flush — without this lift, remote tiles mount `<video>` with still-disabled tracks for
   * ~1 RTT (a black+silent flash). Lifting here lets the still-connected old instance play
   * live media until the rebuilt client takes over.
   */
  public liftIncomingMediaSuppression(): void {
    this.receiveOnly = false;
    this.applyIncomingMediaPolicyToAll();
  }

  /**
   * AVS-4302: in-place demotion teardown. A previously-publishing instance that flips to
   * receive-only (an `<e3d-voip>` embed has no step-down rebuild) must stop transmitting:
   * null every active sender (keeps the transceiver reusable, same rationale as
   * `removeMyTrackFromAllPeers`) and release the camera so its light goes off. The camera is
   * released through its manager only when this client owns it (see `destroy()`), and
   * `me.stream` is cleared so a later same-instance promotion cannot re-send a dead stream.
   */
  private stopPublishingMedia(): void {
    const { me, remoteConnections } = this.store.data;

    Object.values(remoteConnections).forEach(({ connectionId, peerConnection }) => {
      peerConnection?.getSenders?.().forEach((sender) => {
        if (sender.track) {
          log.log('[VOIP] Demotion: muting sender on', connectionId, Date.now().toString().slice(-5));
          sender.replaceTrack(null).catch((err: unknown) => {
            log.log('[VOIP] Demotion replaceTrack(null) failed for', connectionId, err);
          });
        }
      });
    });

    const localCamera = myCameraStream();
    if (me.stream && me.stream === localCamera.stream) {
      localCamera.stopStream();
    } else {
      me.stream?.getTracks?.().forEach((track: MediaStreamTrack) => track.stop());
    }
    this.store.setValues({ me: { ...me, stream: undefined } });
  }

  /**
   * AVS-4302: a standby publishes no media (a standby never grabs the camera — see
   * `initWebRtc`), but it MUST still drive the offer/answer handshake as the lexical
   * initiator. Otherwise a peer pair where the standby owns the higher `connectionId`
   * never negotiates: the master is not the initiator, and the standby never ran an offer
   * pass — so the master's loopback stream never reaches the standby until an unrelated
   * mute/unmute on the master forces an out-of-band offer (the reported bug: the standby's
   * "You" tile shows a placeholder, then snaps to video the instant the master toggles).
   *
   * This mirrors how a receive-only customer participates via `publish()`. Passing
   * `receiveOnly` keeps every offer media-free, so a standby can never accidentally publish
   * a second dealer stream. Future peers are handled by the `ConnectionAdded` listener
   * (guarded by `this.published`), so this only needs to run once per client instance.
   */
  public negotiateAsReceiver(): void {
    // An in-place demotion (was publishing on THIS instance) must stop transmitting too.
    const wasPublishing = this.published && !this.receiveOnly;
    this.published = true;
    this.negotiationPass = 'receive-only';
    // Force the recvonly path for current AND future peers (ConnectionAdded reads this.receiveOnly).
    this.receiveOnly = true;
    if (wasPublishing) {
      this.stopPublishingMedia();
    }
    // Tracks can arrive BEFORE this pass (a standby answers inbound offers ungated), so
    // sweep everything already stored — ontrack covers the rest.
    this.applyIncomingMediaPolicyToAll();
    const remoteConnections = this.store.get('remoteConnections');

    log.log('[VOIP] Negotiating receive-only (standby)', Date.now().toString().slice(-5), remoteConnections);

    Object.keys(remoteConnections).forEach((connectionId) => {
      this.prepareConnectionAndOffer(connectionId, true);
    });

    // Suppressed media is silent — polling analysers would burn CPU for nothing.
    if (!this.suppressIncomingMedia) {
      this.speakerDetector.start();
    }
  }

  private setupListeners(): void {
    // AVS-4332: socket-health telemetry for the MEDIA/VOIP socket, counted separately from the
    // session-broadcast socket so one number can never hide which channel died.
    //
    // Telemetry ONLY. Degraded session sync is a session-state-sync concept owned by the broadcast
    // socket in `utils/ui/rtc.ts`; the media channel dying must never flip session sync into
    // degraded mode. Nothing here may touch `syncStatus` / the degraded poll.
    this.signalerClient.on(EWebsocketClientEvents.WsClientReconnecting, () => {
      recordSocketReconnectAttempt('voip');
    });

    this.signalerClient.on(EWebsocketClientEvents.WsClientGaveUp, () => {
      recordSocketGaveUp('voip');
    });

    this.signalerClient.on(EWebsocketClientEvents.WsClientReconnected, (newConnection: TRawConnection) => {
      const me = this.store.get('me');
      // Update our connection ID — the old one is no longer valid on the server
      this.store.setValues({
        me: { ...me, connectionId: newConnection.connectionId },
      });
      // Close and clear stale peer connections.
      // The server re-dispatches ConnectionAdded for each active peer so RTCClient
      // will create fresh RTCPeerConnections via the normal ConnectionAdded handler.
      const staleConnections = this.store.get('remoteConnections');
      Object.values(staleConnections).forEach(({ peerConnection }) => {
        peerConnection?.close();
      });
      this.store.setValues({ remoteConnections: {} });
      this.pendingCandidates = {};
      // AVS-4302: a reconnect reassigns me.connectionId (the polite/impolite role can flip) and
      // tears down peers; a stale makingOffer/ignoringOffer would misclassify the first
      // post-reconnect offer and re-deadlock. Clear alongside pendingCandidates. The recovery timers
      // are keyed by the OLD (now-invalid) connectionIds, so cancel and empty the whole maps.
      this.makingOffer = {};
      this.ignoringOffer = {};
      Object.values(this.renegotiationTimers).forEach((timer) => clearTimeout(timer));
      Object.values(this.iceGraceTimers).forEach((timer) => clearTimeout(timer));
      Object.values(this.convergenceTimers).forEach((timer) => clearTimeout(timer));
      this.renegotiationTimers = {};
      this.renegotiationRetries = {};
      this.iceGraceTimers = {};
      this.iceRestartCounts = {};
      this.iceGiveUpReported = {};
      this.transportRebuilds = {};
      this.silentTransportEscalations = {};
      this.convergenceTimers = {};
      log.log(
        '[VOIP] Signaling reconnected, cleared stale peer connections',
        newConnection,
        Date.now().toString().slice(-5)
      );
    });

    this.signalerClient.on(EConnectionEvents.ConnectionAdded, ({ connectionId, meta }: TRawConnection) => {
      log.log('[VOIP] Connection Adding - ', connectionId, Date.now().toString().slice(-5));
      const remoteConnections = this.store.get('remoteConnections');
      const existing = remoteConnections[connectionId];

      // A live entry is MERGED, never rebuilt: an on-demand (metaPending) peer already negotiated
      // on this pc, and a duplicate Add would otherwise leak the old pc and wipe stream/mute state
      // (live tile → placeholder). peer:connected fires once — when the identity first lands.
      if (existing?.peerConnection) {
        const wasPending = existing.metaPending;
        this.store.setValues({
          remoteConnections: {
            ...remoteConnections,
            [connectionId]: { ...existing, meta, metaPending: undefined },
          },
        });
        if (wasPending) {
          this.emit('peer:connected', connectionId, this.store.get('remoteConnections'));
        }
        log.log('[VOIP] Connection Added (merged) - ', connectionId, Date.now().toString().slice(-5));
        return;
      }

      // AVS-4430 media-health: a remote appears with NO MediaStream — the gray tile, recorded the
      // moment it appears rather than inferred later from its absence. `stream-attached` closes it.
      reportRtc('peer-added', { connectionId, hasStream: false, metaPending: false, source: 'connection-added' });
      const peerConnection = this.createPeerConnection(connectionId);
      // A browser that will not build peer connections builds none for anyone. Storing the peer
      // without one would leave a permanently gray tile and a signaling loop with nothing to feed.
      if (!peerConnection) return;
      this.store.setValues({
        remoteConnections: {
          ...this.store.get('remoteConnections'),
          [connectionId]: { connectionId, meta, peerConnection },
        },
      });
      if (this.published) this.prepareConnectionAndOffer(connectionId, this.receiveOnly);
      this.emit('peer:connected', connectionId, this.store.get('remoteConnections'));
      log.log('[VOIP] Connection Added - ', connectionId, Date.now().toString().slice(-5));
    });

    this.signalerClient.on(EConnectionEvents.ConnectionRemoved, ({ connectionId }: TRawConnection) => {
      // Only announce the departure of a peer we actually tracked. A tab learns peers from an async
      // ConnectionAdded burst (the handshake carries no peer list — see connect()), so during takeover
      // churn or after a signaling reconnect cleared the store it can legitimately receive a
      // ConnectionRemoved for a connectionId it never stored. With no store entry there is no identity
      // (meta) to tell whether it was THIS dealer's own tab, and we never showed a "peer connected"
      // toast for it — so a "peer disconnected" toast would be a false positive (AVS-4302). Return
      // before removePeer so the untracked path never reaches speakerDetector.stop().
      const tracked = this.store.get('remoteConnections')[connectionId];
      if (!tracked) return;
      this.removePeer(connectionId);
      // An identity-pending peer never announced a connect — announcing its disconnect would be
      // a false "peer disconnected" toast (own-tab churn can't be classified without meta).
      if (tracked.metaPending) return;
      // AVS-4392: a peer the recovery ladder wrote off was already announced by markTransportFailed;
      // its eventual real departure must not toast the customer a second time for the same peer.
      if (tracked.transportFailed) return;
      this.emit('peer:disconnected', connectionId, this.store.get('remoteConnections'), tracked.meta);
    });

    this.signalerClient.on(ERTCEvents.PeerSignal, this.onPeerSignal.bind(this));

    this.signalerClient.on(ERTCEvents.StreamUpdate, (data: Partial<TRTCConnection>, from?: string) => {
      const remoteConnections = this.store.get('remoteConnections');
      const remoteConnection = remoteConnections[from!];
      if (remoteConnection) {
        log.log(`[VOIP] Remote stream update - ${from}`, data);
        // AVS-4392: `transportFailed` is a LOCAL observation about OUR transport to this peer, so
        // the wire must never set or clear it — this handler spreads whatever arrives, which would
        // otherwise let a peer hide itself from us, or resurrect a tile we already proved dead.
        const safeData = { ...data };
        delete safeData.transportFailed;
        this.store.setValues({
          remoteConnections: {
            ...remoteConnections,
            [from!]: {
              ...remoteConnection,
              ...safeData,
            },
          },
        });
      }
    });

    this.speakerDetector.on('activeSpeakerChanged', (speakerId: string | null) => {
      this.handleActiveSpeakerChange(speakerId);
    });
  }

  private async onPeerSignal(data: RTCSessionDescriptionInit | RTCIceCandidate, from?: string) {
    const remoteConnections = this.store.get('remoteConnections');
    let connection: TRTCConnection | undefined = remoteConnections[from!];

    if (!connection?.peerConnection) {
      // AVS-4302 takeover reliability: an offer from an unknown peer means its ConnectionAdded is
      // late (async burst) or lost — dropping it strands the pair, because the peer's bounded
      // re-offers all hit this same guard. Admit the peer on demand; ICE/answers from unknown
      // peers are meaningless without prior negotiation state and stay dropped.
      connection = 'type' in data && data.type === 'offer' && from ? this.admitPeerFromOffer(from) : undefined;
      if (!connection) {
        console.warn('[VOIP] Peer Connection not found for ', from);
        return;
      }
    }
    // If the data contains a `type` property, it's an SDP (offer or answer)
    if ('type' in data) {
      switch (data.type) {
        case 'offer':
          log.log('[VOIP] Received an offer from ', from, data, Date.now().toString().slice(-5));
          await this.handleOfferSignals(data, from);
          break;
        case 'answer':
          log.log('[VOIP] Received an answer from ', from, data, Date.now().toString().slice(-5));
          await this.handleAnswerSignals(data, from);
          break;
        default:
          console.warn('[VOIP] Received an unknown signal type from ', from, data);
      }
    } else {
      // AVS-4302: await so pending ICE candidates are flushed only after remoteDescription is set.
      await this.handleIceCandidate(data, from);
    }

    // AVS-4392: the flush is genuinely async now, and this handler's own promise floats in the
    // signaler — so contain the rejection here rather than letting it escape unhandled.
    void this.handlePendingIceCandidates(from).catch((error: unknown) => {
      log.warn('[VOIP] Pending ICE candidate flush failed', from, error);
    });
  }

  /**
   * AVS-4302 takeover reliability: create a peer entry for an inbound offer that beat (or lost)
   * its ConnectionAdded. The entry is `metaPending` — fully negotiable but identity-less: the
   * render layer skips it and no peer toast fires until ConnectionAdded merges the real meta in.
   * When this client already ran its per-peer negotiation pass (published), run it for the
   * admitted peer too so our senders attach BEFORE we answer — otherwise the on-demand pair
   * stays recvonly-forever on our side and the peer never sees our media.
   */
  private admitPeerFromOffer(connectionId: string): TRTCConnection | undefined {
    const pendingCount = Object.values(this.store.get('remoteConnections')).filter((c) => c.metaPending).length;
    if (pendingCount >= this.maxMetaPendingPeers) {
      log.warn('[VOIP] Refusing on-demand peer admission (cap reached)', connectionId, Date.now().toString().slice(-5));
      return undefined;
    }
    log.log('[VOIP] Offer from unknown peer — admitting on demand', connectionId, Date.now().toString().slice(-5));
    reportRtc('peer-added', { connectionId, hasStream: false, metaPending: true, source: 'on-demand-offer' });
    const peerConnection = this.createPeerConnection(connectionId);

    if (!peerConnection) {
      return undefined;
    }

    this.store.setValues({
      remoteConnections: {
        ...this.store.get('remoteConnections'),
        [connectionId]: { connectionId, metaPending: true, peerConnection },
      },
    });
    if (this.published) {
      void this.prepareConnectionAndOffer(connectionId, this.receiveOnly);
    }
    return this.store.get('remoteConnections')[connectionId];
  }

  private async handleIceCandidate(data: RTCSessionDescriptionInit | RTCIceCandidate, from?: string) {
    const remoteConnections = this.store.get('remoteConnections');
    const connection = remoteConnections[from!];

    if (!connection?.peerConnection) {
      return;
    }

    // Handle ICE candidates
    reportRtc('ice-candidate', { connectionId: from, direction: 'remote', candidate: data });
    if (connection.peerConnection.remoteDescription) {
      const failure = await this.tryAddIceCandidate(connection.peerConnection, data as RTCIceCandidate);
      if (!failure) return;
      // AVS-4302: while we are ignoring a colliding offer from this peer, its in-flight
      // candidates belong to the DISCARDED offer — they are dead, not early, so they are
      // swallowed and must never be queued for retry. The flag is cleared once the surviving
      // negotiation reaches stable.
      if (this.ignoringOffer[from!]) {
        log.log('[VOIP] Swallowed ICE candidate for an ignored offer', from, Date.now().toString().slice(-5));
        return;
      }
      // The transport can be REPLACED while we await (rebuildPeerTransport swaps the pc and clears
      // this queue; removePeer drops the entry). Queueing now would insert a dead-transport
      // candidate into the FRESH pc's queue, where the flush's identity guard can no longer catch
      // it (current === the new pc) — retained and retried for the peer's whole lifetime.
      if (this.store.get('remoteConnections')[from!]?.peerConnection !== connection.peerConnection) {
        log.log('[VOIP] Dropped an ICE candidate for a replaced transport', from, Date.now().toString().slice(-5));
        return;
      }
      // AVS-4392 B1: during an ICE RESTART `remoteDescription` is the STALE previous answer, so
      // this branch is taken while the peer's new answer is still in flight and the candidate is
      // rejected against the old ufrag. Queue it — rethrowing here (the old behaviour) surfaced as
      // an unhandled rejection in an async void handler and lost the candidate permanently, which
      // alone can stop ICE completing: the far side's checks stay NAT-filtered because we never
      // send checks toward candidates we never stored.
      log.log('[VOIP] Queueing ICE candidate rejected by the current transport', from, failure.message);
    }
    this.queuePendingCandidate(data as RTCIceCandidate, from);
  }

  /** Adds a candidate, returning the rejection instead of throwing (`null` on success). */
  private async tryAddIceCandidate(
    peerConnection: RTCPeerConnection,
    candidate: RTCIceCandidate
  ): Promise<Error | null> {
    try {
      await peerConnection.addIceCandidate(candidate);
      return null;
    } catch (error) {
      return error instanceof Error ? error : new Error(String(error));
    }
  }

  /**
   * AVS-4392: bounded per-peer candidate queue. A candidate that can never apply (malformed, or
   * gathered for a transport that has since been replaced) would otherwise be retried on every
   * subsequent signal for the peer's whole lifetime, so the queue drops oldest-first at the cap.
   */
  private queuePendingCandidate(candidate: RTCIceCandidate, from?: string) {
    if (!this.pendingCandidates[from!]) {
      this.pendingCandidates[from!] = [];
    }
    const queue = this.pendingCandidates[from!];
    queue.push(candidate);
    if (queue.length === this.maxPendingCandidates) {
      log.warn('[VOIP] Pending ICE candidate queue at capacity', from, Date.now().toString().slice(-5));
    }
    if (queue.length > this.maxPendingCandidates) {
      queue.splice(0, queue.length - this.maxPendingCandidates);
      log.log('[VOIP] Dropped oldest pending ICE candidate (queue full)', from, Date.now().toString().slice(-5));
    }
  }

  private async handlePendingIceCandidates(from?: string) {
    if (!from) {
      // Await the fan-out so this promise means "every peer was flushed", not "every flush was
      // started"; the per-peer catch keeps one failure from aborting the rest.
      await Promise.all(
        Object.keys(this.pendingCandidates).map((connectionId) =>
          this.handlePendingIceCandidates(connectionId).catch((error: unknown) => {
            log.warn('[VOIP] Pending ICE candidate flush failed', connectionId, error);
          })
        )
      );
      return;
    }
    log.log('[VOIP] Handling Pending ICE Candidates', from, Date.now().toString().slice(-5));

    const peerConnection = this.store.get('remoteConnections')[from]?.peerConnection;
    const candidates = this.pendingCandidates[from];

    if (!peerConnection?.remoteDescription || !candidates?.length) {
      return;
    }

    // AVS-4392: take the batch BEFORE awaiting so a candidate arriving mid-flush is not added
    // twice, and await each add — the old `forEach(async …)` never awaited, so every rejection
    // became an unhandled rejection and the queue was cleared whether or not the adds landed.
    this.pendingCandidates[from] = [];
    const stillPending: RTCIceCandidate[] = [];
    for (const candidate of candidates) {
      const failure = await this.tryAddIceCandidate(peerConnection, candidate);
      if (failure) stillPending.push(candidate);
    }
    if (!stillPending.length) return;

    // The transport can be REPLACED while we await (rebuildPeerTransport swaps the pc and clears
    // this queue; removePeer drops the entry entirely). Re-queueing candidates gathered for the
    // dead transport would poison the fresh one forever — they can never apply to it — and would
    // resurrect the queue of a departed peer. Re-read at fire time and drop the batch if it moved.
    const current = this.store.get('remoteConnections')[from]?.peerConnection;
    if (current !== peerConnection) {
      log.log('[VOIP] Dropped pending ICE candidates for a replaced transport', from, stillPending.length);
      return;
    }
    this.pendingCandidates[from] = [...stillPending, ...this.pendingCandidates[from]].slice(-this.maxPendingCandidates);
    log.warn('[VOIP] Retained ICE candidates the current transport rejected', from, stillPending.length);
  }

  private async handleAnswerSignals(data: RTCSessionDescriptionInit | RTCIceCandidate, from?: string) {
    const remoteConnections = this.store.get('remoteConnections');
    const connection = remoteConnections[from!];

    if (!connection?.peerConnection) {
      console.warn('[VOIP] Peer Connection not found for answer from', from);
      return;
    }

    const signalingState = connection.peerConnection.signalingState;

    switch (signalingState) {
      case 'stable':
        console.warn('[VOIP] Received an answer in stable state, ignoring:', from, data);
        break;
      case 'have-local-offer':
        log.log('[VOIP] Received a have-local-offer answer from ', from, data, Date.now().toString().slice(-5));
        // AVS-4302 (review gate C1): guard against a STALE answer — one generated against an offer we
        // have already superseded (a recovery re-offer raced an in-flight answer). setRemoteDescription
        // rejects on the m-line/ufrag mismatch; without this guard the throw escapes onPeerSignal
        // uncaught. Swallow it and arm recovery instead — the pending newer offer's own answer (or the
        // timer) resolves the pair.
        try {
          await connection.peerConnection.setRemoteDescription(data as RTCSessionDescriptionInit);
        } catch (error) {
          log.warn('[VOIP] Ignoring stale/mismatched answer; arming recovery', from, error);
          reportRtc('sdp-rejected', { connectionId: from, direction: 'remote', reason: String(error) });
          this.armRenegotiationTimer(from!);
          break;
        }
        reportRtc('sdp', { connectionId: from, direction: 'remote', description: data });
        // AVS-4302: our own (kept) offer was answered — this negotiation is the survivor, so
        // stop swallowing ICE candidate errors for this peer.
        this.ignoringOffer[from!] = false;
        break;
      default:
        console.warn('[VOIP] Received an unknown answer signal type from ', from, data);
    }
  }

  private handleActiveSpeakerChange(speakerId: string | null): void {
    const remoteConnections = this.store.get('remoteConnections');

    // Reset all connections to not be active speakers and mark the last active speaker
    const updatedConnections = Object.keys(remoteConnections).reduce(
      (acc, connectionId) => {
        const connection = remoteConnections[connectionId];

        acc[connectionId] = {
          ...connection,
          isActiveSpeaker: connectionId === speakerId,
          lastActiveSpeaker: connection.isActiveSpeaker && !speakerId,
        };
        return acc;
      },
      {} as Record<string, TRTCConnection>
    );

    this.store.setValues({
      remoteConnections: updatedConnections,
    });
  }

  private startStreamingMe(peerConnection: RTCPeerConnection) {
    const stream = myCameraStream().stream;

    if (!stream) {
      return false;
    }

    stream.getTracks().forEach((track: MediaStreamTrack) => {
      this.sendTrackToPeer(track, peerConnection);
    });
  }

  private sendTrackToPeer(track: MediaStreamTrack, peerConnection: RTCPeerConnection): boolean {
    if (!myCameraStream().stream) {
      return false;
    }
    if (track.readyState === 'ended') {
      log.log('[VOIP] Skipping ended track', track.kind, Date.now().toString().slice(-5));
      return false;
    }
    log.log('[VOIP] Adding track to peer connection', track, peerConnection, Date.now().toString().slice(-5));
    if (track.kind === 'audio' || track.kind === 'video') {
      const senders = peerConnection.getSenders();
      const sender = senders.find((sender) => sender.track?.kind === track.kind);
      if (!sender) {
        // Look for a reusable transceiver (sender nulled via replaceTrack(null) during mute,
        // or created by offerToReceiveVideo/Audio during initial negotiation)
        const reusableTransceiver = peerConnection
          .getTransceivers()
          .find(
            (t) => t.sender.track === null && t.receiver.track?.kind === track.kind && t.currentDirection !== 'stopped'
          );
        if (reusableTransceiver) {
          log.log('[VOIP] Reusing existing sender for', track.kind, Date.now().toString().slice(-5));
          reusableTransceiver.sender.replaceTrack(track);
          // If the transceiver direction doesn't allow sending (e.g. recvonly from
          // offerToReceiveVideo), update it and signal that renegotiation is needed.
          if (reusableTransceiver.direction !== 'sendrecv' && reusableTransceiver.direction !== 'sendonly') {
            // Associate track with a stream so the remote ontrack event includes event.streams[0].
            // Without this, replaceTrack on a reused transceiver produces msid:- in SDP.
            if (typeof reusableTransceiver.sender.setStreams === 'function') {
              reusableTransceiver.sender.setStreams(myCameraStream().stream!);
            }
            reusableTransceiver.direction = 'sendrecv';
            return true; // Renegotiation needed to enable sending
          }
          return false; // Direction already allows sending, no renegotiation needed
        } else {
          peerConnection.addTrack(track, myCameraStream().stream!);
          return true; // New transceiver → needs offer
        }
      } else {
        sender.replaceTrack(track);
        return false; // No renegotiation needed
      }
    }
    return false;
  }

  private async sendOffer(peerConnection: RTCPeerConnection, connectionId: string) {
    log.log('[VOIP] Sending Offer to ', connectionId, Date.now().toString().slice(-5));

    // AVS-4302: flag our offer in flight so a colliding inbound offer is detected even before
    // signalingState flips to have-local-offer. The finally wraps BOTH createOffer and
    // setLocalDescription so an early throw can never strand the flag true (which would make us
    // wrongly treat the next inbound offer as a collision and re-deadlock).
    this.makingOffer[connectionId] = true;
    try {
      // Create an SDP offer to initiate the connection
      const offer = await peerConnection.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
      await peerConnection.setLocalDescription(offer);
      reportRtc('sdp', { connectionId, direction: 'local', description: offer });

      // Send the SDP offer to the remote peer via the signaling server
      this.signalerClient.signal({
        action: ERTCEvents.PeerSignal,
        to: [connectionId],
        data: offer,
      });
      // AVS-4302: we are now awaiting an answer. Arm the recovery timer so a lost/dropped answer
      // (the takeover-glare strand) re-offers instead of stranding at have-local-offer forever. The
      // timer is cleared the moment we reach stable (onsignalingstatechange), so a normally-answered
      // offer never fires it.
      this.armRenegotiationTimer(connectionId);
    } finally {
      this.makingOffer[connectionId] = false;
    }
  }

  // AVS-4302: arm/clear the per-peer "awaiting answer" recovery timer. Re-arming first clears any
  // pending timer so we never stack two for one peer.
  private armRenegotiationTimer(connectionId: string): void {
    if (this.renegotiationTimers[connectionId]) {
      clearTimeout(this.renegotiationTimers[connectionId]);
    }
    this.renegotiationTimers[connectionId] = setTimeout(() => {
      void this.forceReoffer(connectionId);
    }, this.renegotiationTimeoutMs);
  }

  private clearRenegotiationTimer(connectionId: string): void {
    if (this.renegotiationTimers[connectionId]) {
      clearTimeout(this.renegotiationTimers[connectionId]);
      delete this.renegotiationTimers[connectionId];
    }
    this.renegotiationRetries[connectionId] = 0;
  }

  // AVS-4302 takeover reliability: the waiting (non-initiator) side of a never-negotiated pair
  // arms this deadline — a lost initiator offer otherwise strands the pair at 'stable' forever,
  // because only sendOffer arms recovery. Shares renegotiationTimers so every existing cleanup
  // path (destroy, reconnect reset, removePeer, transport rebuild, stable-reached) covers it,
  // and never replaces an already-armed timer (an offer of our own is already being recovered).
  private armWaitingOfferWatchdog(connectionId: string): void {
    if (this.renegotiationTimers[connectionId]) return;
    this.renegotiationTimers[connectionId] = setTimeout(() => {
      delete this.renegotiationTimers[connectionId];
      void this.offerIfNeverNegotiated(connectionId);
    }, this.waitingOfferTimeoutMs);
  }

  // AVS-4302 takeover reliability: telemetry deadline (see convergenceTimers). Re-armed by every
  // negotiation pass; self-clearing on fire, and cleared wherever the peer's other timers are.
  private armConvergenceReport(connectionId: string): void {
    this.clearConvergenceTimer(connectionId);
    this.convergenceTimers[connectionId] = setTimeout(() => {
      delete this.convergenceTimers[connectionId];
      this.reportConvergence(connectionId);
    }, this.convergenceReportMs);
  }

  private clearConvergenceTimer(connectionId: string): void {
    if (this.convergenceTimers[connectionId]) {
      clearTimeout(this.convergenceTimers[connectionId]);
      delete this.convergenceTimers[connectionId];
    }
  }

  private reportConvergence(connectionId: string): void {
    const entry = this.store.get('remoteConnections')[connectionId];
    const peerConnection = entry?.peerConnection;
    if (!entry || !peerConnection) return;
    // Transport up = negotiation worked; an absent stream on a connected pair is a legitimate
    // media decision (e.g. a peer publishing nothing), not a stall — stay silent for it.
    if (peerConnection.connectionState === 'connected') return;
    // AVS-4430: this payload already was exactly what a recorder wants — connectionId, all three
    // states, hasStream, per-track kind/enabled/muted, metaPending — and it only ever reached a
    // console that nobody kept. It is assembled ONCE and goes to both sinks: the warn stays because
    // it is what a live debugging session reads, and the record is what survives the tab closing.
    const convergence = {
      connectionId,
      signalingState: peerConnection.signalingState,
      connectionState: peerConnection.connectionState,
      iceConnectionState: peerConnection.iceConnectionState,
      hasStream: !!entry.stream,
      tracks:
        entry.stream
          ?.getTracks?.()
          .map((track) => ({ kind: track.kind, enabled: track.enabled, muted: track.muted })) ?? [],
      metaPending: !!entry.metaPending,
    };
    log.warn('[VOIP] Peer not converged after negotiation deadline', convergence);
    reportRtc('convergence-failed', convergence);
    this.escalateStalledTransport(connectionId);
  }

  /**
   * AVS-4441: put a pair that finished its handshake and never started its transport onto the
   * recovery ladder.
   *
   * The field signature (Audi Switzerland, 2026-09-09) was signalingState 'stable',
   * connectionState 'new', iceConnectionState 'new', zero bytes ever. Every ladder in this class
   * keys on 'failed' or 'disconnected'; this pair reached neither, so `handleIceConnectionStateChange`
   * fired no branch at all and the tile sat there until the dealer reloaded. The DETECTOR already
   * existed — the convergence deadline above — it simply had no actor.
   *
   * Three gates keep healthy pairs off the ladder, because 'new'/'new' is also what a perfectly
   * normal handshake measures before the first remote candidate arrives:
   *
   * - `published`: this client has not negotiated anything yet, so there is nothing to restart.
   * - `signalingState !== 'stable'`: an offer is still in flight; restarting now fights it.
   * - `remoteDescription === null`: nothing ever arrived — that is the LOST-OFFER case, which
   *   {@link armWaitingOfferWatchdog} owns on a timer a rebuild here would silently clear.
   */
  private escalateStalledTransport(connectionId: string): void {
    const entry = this.store.get('remoteConnections')[connectionId];
    const pc = entry?.peerConnection;
    if (!entry || !pc) return;
    if (entry.transportFailed) return;
    if (pc.connectionState === 'connected') return;
    if (!this.published) return;
    if (pc.signalingState !== 'stable') return;
    if (pc.remoteDescription === null) return;
    // The ICE agent must still be at 'new'. That is the field signature, and it is the ONLY ICE
    // state without a ladder: 'failed' and 'disconnected' are handled by
    // handleIceConnectionStateChange, and 'checking' always resolves into one of them. Without
    // this gate a slow-but-working TURN pair still negotiating at the 8 s deadline would get its
    // candidate pairs thrown away and restarted — the deadline is a stall detector, not a
    // patience limit.
    if (pc.iceConnectionState !== 'new') return;

    reportRtc('stalled-transport-escalation', {
      connectionId,
      signalingState: pc.signalingState,
      connectionState: pc.connectionState,
      iceConnectionState: pc.iceConnectionState,
      iceRestarts: this.iceRestartCounts[connectionId] ?? 0,
    });

    // The exhaustion check runs HERE, not inside maybeRestartIce. Handing a spent budget to that
    // ladder writes the peer off on the spot and skips the rebuild rung entirely — and a rebuild
    // is exactly what a transport that never started needs.
    if ((this.iceRestartCounts[connectionId] ?? 0) >= this.maxIceRestarts) {
      this.rebuildPeerTransport(connectionId);
      return;
    }

    this.maybeRestartIce(connectionId);
  }

  /**
   * AVS-4441: the actor for the silent-transport watchdog in `utils/ui/voipStats.ts`.
   *
   * On prod a 3D stream delivered 10 frames and stopped: ICE 'connected', 54 ms RTT, zero loss,
   * zero NACKs. Every health signal this class watches stayed green, so no ladder ran. The AVS-4419
   * sampler was already measuring the pair every 5 s — it had nobody to tell. Public because that
   * sampler is the caller; the policy (how many windows, which rung, when to write off) stays here
   * with the rest of the recovery ladder, and the sampler stays a measurement.
   *
   * @param connectionId - the peer whose transport has carried nothing for a full window
   */
  public escalateSilentTransport(connectionId: string): void {
    const entry = this.store.get('remoteConnections')[connectionId];
    const pc = entry?.peerConnection;
    if (!entry || !pc) return;
    if (entry.transportFailed) return;
    // Silence only means something on a transport that is actually up. A pair that has not
    // connected yet belongs to escalateStalledTransport; letting both ladders spend their budgets
    // on the same peer would double the ICE restarts it sees.
    if (pc.connectionState !== 'connected') return;

    const spent = this.silentTransportEscalations[connectionId] ?? 0;

    if (spent >= this.maxSilentTransportEscalations) {
      log.warn('[VOIP] Transport silent after every recovery attempt, writing peer off', {
        connectionId,
        escalations: spent,
      });
      reportRtc('silent-transport-give-up', {
        connectionId,
        escalations: spent,
        connectionState: pc.connectionState,
        iceConnectionState: pc.iceConnectionState,
      });
      this.markTransportFailed(connectionId);
      return;
    }

    this.silentTransportEscalations[connectionId] = spent + 1;
    log.log('[VOIP] Transport silent, restarting ICE', connectionId, spent + 1);
    reportRtc('silent-transport-escalation', {
      connectionId,
      attempt: spent + 1,
      connectionState: pc.connectionState,
      iceConnectionState: pc.iceConnectionState,
    });
    this.maybeRestartIce(connectionId);
  }

  private async offerIfNeverNegotiated(connectionId: string): Promise<void> {
    const pc = this.store.get('remoteConnections')[connectionId]?.peerConnection;
    // Fire-time re-check: a set remoteDescription means the initiator delivered, and offering
    // from a non-stable state would throw — both bow out. Glare stays safe when we DO offer:
    // we are the polite (lower-cid) peer, so a late-colliding initiator offer wins via rollback.
    if (!pc || pc.remoteDescription !== null || pc.signalingState !== 'stable') return;
    log.log(
      '[VOIP] Initiator offer never arrived — offering from the waiting side',
      connectionId,
      Date.now().toString().slice(-5)
    );
    await this.forceReoffer(connectionId);
  }

  /**
   * AVS-4302: the onnegotiationneeded happy path. Drives a fresh offer ONLY from a clean stable
   * state (a track added on this side, a direction change, or restartIce()). The makingOffer guard
   * keeps it to one offer at a time. Recovery from a NON-stable state is forceReoffer's job — this
   * deliberately bails when not stable so the two never collide.
   */
  private async driveNegotiation(connectionId: string): Promise<void> {
    const pc = this.store.get('remoteConnections')[connectionId]?.peerConnection;
    if (!pc || pc.signalingState !== 'stable' || this.makingOffer[connectionId]) return;
    await this.sendOffer(pc, connectionId);
  }

  /**
   * AVS-4302: recovery re-offer for a peer stuck awaiting an answer. Legal from stable OR
   * have-local-offer (createOffer supersedes the pending offer). It NEVER rolls back — so a slow
   * answer to the prior offer cannot corrupt a survivor (review gate C1). It fires on whoever is
   * stranded, independent of the peer's state (review gate C2), and is bounded by
   * maxRenegotiationRetries. sendOffer re-arms the timer, so a still-unanswered re-offer retries
   * until the cap.
   */
  private async forceReoffer(connectionId: string): Promise<void> {
    const pc = this.store.get('remoteConnections')[connectionId]?.peerConnection;
    if (!pc || pc.signalingState === 'closed' || pc.connectionState === 'closed') return;
    if (this.makingOffer[connectionId]) return;
    if ((this.renegotiationRetries[connectionId] ?? 0) >= this.maxRenegotiationRetries) {
      log.warn('[VOIP] Giving up renegotiation after retries', connectionId, Date.now().toString().slice(-5));
      return;
    }
    this.renegotiationRetries[connectionId] = (this.renegotiationRetries[connectionId] ?? 0) + 1;
    // The old dropped offer's ICE is moot now that we are superseding it.
    this.ignoringOffer[connectionId] = false;
    await this.sendOffer(pc, connectionId);
  }

  // AVS-4302: the single stable-reaching hook (wired to onsignalingstatechange). Every path to
  // stable — our offer answered, or we sent an answer — clears the recovery timer here, so disarm
  // coverage cannot be missed by an individual handler.
  private handleSignalingStateChange(connectionId: string): void {
    const pc = this.store.get('remoteConnections')[connectionId]?.peerConnection;
    if (!pc) return;
    if (pc.signalingState === 'stable') {
      this.clearRenegotiationTimer(connectionId);
    }
  }

  /**
   * AVS-4302: ICE is ground truth for "media actually flows" — signalingState is not. A pair that
   * negotiated SDP but whose transport fails/disconnects is recovered with restartIce (→
   * onnegotiationneeded → a fresh offer carrying new ICE). 'disconnected' gets one grace window
   * before escalating (it is often transient); 'failed' escalates at once. Bounded by maxIceRestarts
   * so a permanently dead peer cannot drive unbounded churn. Inert on a closed pc.
   */
  private handleIceConnectionStateChange(connectionId: string): void {
    const pc = this.store.get('remoteConnections')[connectionId]?.peerConnection;
    if (!pc || pc.connectionState === 'closed') return;
    const state = pc.iceConnectionState;
    if (state === 'connected' || state === 'completed') {
      this.clearIceGraceTimer(connectionId);
      // AVS-4392 B2: recovery SUCCEEDED — hand both ladders their full budget back. They were
      // previously reset only on session reset, reconnect or peer removal, so the counts only ever
      // ratcheted upward across a call: a long session accumulated unrelated transient blips until
      // the budget was spent, and the next blip was permanently fatal with no recovery attempted.
      delete this.iceRestartCounts[connectionId];
      delete this.iceGiveUpReported[connectionId];
      delete this.transportRebuilds[connectionId];
      // AVS-4392 B3: only clear the give-up flag when the AGGREGATE transport is healthy again.
      // ICE can flap back to 'connected' on a pc whose DTLS is dead — that exact asymmetry is why
      // rebuildPeerTransport exists — and clearing here on such a pc would un-hide a tile that can
      // never carry media, with no further connectionState event left to re-hide or rebuild it.
      // This guard is also what makes the write-off sticky: a peer is only flagged while its
      // connectionState is 'failed', DTLS failure is terminal, and the flag blocks the rebuild that
      // would swap the pc — so the flag outlives every ICE flap and only a fresh transport clears it.
      if (pc.connectionState !== 'failed') this.clearTransportFailed(connectionId);
      return;
    }
    if (state === 'failed') {
      this.clearIceGraceTimer(connectionId);
      this.maybeRestartIce(connectionId);
      return;
    }
    if (state === 'disconnected') {
      if (this.iceGraceTimers[connectionId]) return;
      this.iceGraceTimers[connectionId] = setTimeout(() => {
        delete this.iceGraceTimers[connectionId];
        const current = this.store.get('remoteConnections')[connectionId]?.peerConnection;
        if (current && current.iceConnectionState === 'disconnected') this.maybeRestartIce(connectionId);
      }, this.iceDisconnectGraceMs);
    }
  }

  /**
   * AVS-4311 follow-up (gray-tile bug): `connectionState` aggregates ICE AND DTLS transport
   * states. A DTLS failure surfaces as connectionState 'failed' while iceConnectionState stays
   * 'connected' — a state the ICE-watching recovery (handleIceConnectionStateChange) never sees,
   * and one restartIce() cannot fix (the DTLS association is dead; verified live). Without this
   * hook such a pair stayed signaling-stable with muted receiver tracks forever: the remote
   * rendered as a permanent gray tile while its mute badges (signaling channel) kept working.
   *
   * 'closed' keeps the pre-existing remove behaviour. An ICE-level 'failed' is left to the
   * ICE-restart path — rebuilding there would race maybeRestartIce over the same pc.
   */
  private handleConnectionStateChange(connectionId: string): void {
    const peerConnection = this.store.get('remoteConnections')[connectionId]?.peerConnection;
    if (!peerConnection) return;
    if (peerConnection.iceConnectionState === 'closed') {
      log.log('[VOIP] Peer Connection Closed - ', connectionId, Date.now().toString().slice(-5));
      this.removePeer(connectionId);
      peerConnection.close();
      return;
    }
    if (peerConnection.connectionState === 'failed' && peerConnection.iceConnectionState !== 'failed') {
      this.rebuildPeerTransport(connectionId);
    }
  }

  /**
   * Replace a DTLS-dead RTCPeerConnection with a fresh one on the SAME store entry and
   * re-negotiate. Both sides of the pair observe connectionState 'failed', so both rebuild:
   * the lexical initiator re-offers explicitly (prepareConnectionAndOffer), the other side's
   * track re-add fires onnegotiationneeded → driveNegotiation — glare-safe under AVS-4302
   * perfect negotiation. Bounded by maxTransportRebuilds.
   */
  private rebuildPeerTransport(connectionId: string): void {
    const oldPeerConnection = this.store.get('remoteConnections')[connectionId]?.peerConnection;
    if (!oldPeerConnection) return;
    // AVS-4392 B3: the give-up FLAG is the latch here, not the budget — handleIceConnectionStateChange
    // returns the budget unconditionally, so an ICE flap on a DTLS-dead corpse refills it and the next
    // connectionState event would rebuild (and, via createPeerConnection, un-hide) a written-off peer.
    if (this.store.get('remoteConnections')[connectionId]?.transportFailed) return;
    if ((this.transportRebuilds[connectionId] ?? 0) >= this.maxTransportRebuilds) {
      // AVS-4392 B3: the give-up used to just log and return, leaving a peer that keeps its tile
      // and keeps answering SDP — a permanent gray rectangle with live mute badges. An already
      // flagged peer turns back at the latch above, so this records the death exactly once.
      log.warn('[VOIP] Giving up transport rebuild after retries', connectionId, Date.now().toString().slice(-5));
      reportRtc('transport-rebuild-give-up', {
        connectionId,
        rebuilds: this.transportRebuilds[connectionId] ?? 0,
        maxTransportRebuilds: this.maxTransportRebuilds,
        connectionState: oldPeerConnection.connectionState,
        iceConnectionState: oldPeerConnection.iceConnectionState,
      });
      this.markTransportFailed(connectionId);
      return;
    }
    this.transportRebuilds[connectionId] = (this.transportRebuilds[connectionId] ?? 0) + 1;
    recordVoipTransportRebuild();
    log.log('[VOIP] DTLS transport failed; rebuilding peer connection', connectionId, Date.now().toString().slice(-5));
    reportRtc('transport-rebuild', {
      connectionId,
      attempt: this.transportRebuilds[connectionId],
      maxTransportRebuilds: this.maxTransportRebuilds,
      connectionState: oldPeerConnection.connectionState,
      iceConnectionState: oldPeerConnection.iceConnectionState,
    });

    // Detach the old pc's handlers BEFORE closing (mirrors destroy()): a close-induced event on
    // the dying pc would re-enter handleConnectionStateChange while the store still points at it
    // and removePeer() the very entry (connectionId, meta) the rebuild reuses.
    oldPeerConnection.onnegotiationneeded = null;
    oldPeerConnection.onsignalingstatechange = null;
    oldPeerConnection.oniceconnectionstatechange = null;
    oldPeerConnection.onicecandidate = null;
    oldPeerConnection.ontrack = null;
    oldPeerConnection.onconnectionstatechange = null;
    oldPeerConnection.close();
    // Reset per-peer negotiation state so the fresh transport starts clean (mirrors removePeer):
    // stale glare flags would misclassify the first post-rebuild offer, and the old transport's
    // timers/budgets belong to the dead pc.
    delete this.makingOffer[connectionId];
    delete this.ignoringOffer[connectionId];
    this.clearRenegotiationTimer(connectionId);
    this.clearIceGraceTimer(connectionId);
    this.clearConvergenceTimer(connectionId);
    delete this.iceRestartCounts[connectionId];
    delete this.iceGiveUpReported[connectionId];
    delete this.silentTransportEscalations[connectionId];
    delete this.pendingCandidates[connectionId];

    this.createPeerConnection(connectionId);
    if (this.published) {
      void this.prepareConnectionAndOffer(connectionId, this.receiveOnly);
    }
  }

  /**
   * AVS-4392 B3: mark a peer the recovery ladder has abandoned. The store entry survives — its
   * mute badges ride the signaling channel and still work — but `filterDeadTransportTiles`
   * (utils/ui/voipRemotes) drops its tile and `maybeRestartIce` stops churning it. The customer is
   * told through the existing `peer:disconnected` toast rather than watching a participant vanish
   * for no visible reason; identity-pending peers are suppressed exactly as ConnectionRemoved does.
   */
  private markTransportFailed(connectionId: string): void {
    const remoteConnections = this.store.get('remoteConnections');
    const connection = remoteConnections[connectionId];
    if (!connection || connection.transportFailed) return;
    recordVoipPeerWrittenOff();
    // AVS-4430 media-health: `transportFailed` is one of the three free primary signals (with
    // `stream == null` and the framesDecoded delta) and, unlike a gray tile, it names the peer the
    // recovery ladder wrote off. Reported at the flag's single writer, so it cannot double-fire.
    reportRtc('transport-failed', {
      connectionId,
      hasStream: !!connection.stream,
      metaPending: !!connection.metaPending,
      connectionState: connection.peerConnection?.connectionState ?? '',
      iceConnectionState: connection.peerConnection?.iceConnectionState ?? '',
    });
    this.store.setValues({
      remoteConnections: { ...remoteConnections, [connectionId]: { ...connection, transportFailed: true } },
    });
    if (!connection.metaPending) {
      this.emit('peer:disconnected', connectionId, this.store.get('remoteConnections'), connection.meta);
    }
  }

  /** Clears the give-up flag. No-ops when unset so a healthy call never ticks the store. */
  private clearTransportFailed(connectionId: string): void {
    const remoteConnections = this.store.get('remoteConnections');
    const connection = remoteConnections[connectionId];
    if (!connection?.transportFailed) return;
    this.store.setValues({
      remoteConnections: { ...remoteConnections, [connectionId]: { ...connection, transportFailed: false } },
    });
  }

  private maybeRestartIce(connectionId: string): void {
    const connection = this.store.get('remoteConnections')[connectionId];
    const pc = connection?.peerConnection;
    if (!pc || pc.connectionState === 'closed') return;
    // AVS-4392 B3: the rebuild ladder already gave up on this peer. Every rebuild deletes
    // iceRestartCounts (below), so this ladder was handed a full budget again by the very rebuild
    // that exhausted the other one — the two then churned a corpse for minutes, visible in the
    // field log as "Giving up transport rebuild" followed by "Restarting ICE" five seconds later.
    if (connection?.transportFailed) return;
    if ((this.iceRestartCounts[connectionId] ?? 0) >= this.maxIceRestarts) {
      // AVS-4430: this used to be a BARE `return` — no log, no counter, no event. It is the exact
      // path a VPN drop takes, the dominant failure mode, and it left zero trace even with a fully
      // debug console; the DTLS ladder seventy lines above already announces its own give-up.
      // Latched so an exhausted peer that keeps flapping ICE reports once, not once per event.
      if (!this.iceGiveUpReported[connectionId]) {
        this.iceGiveUpReported[connectionId] = true;
        // AVS-4419: the recorder event below exists only on dev/prelive builds. On prod this warn
        // and the session counter are the whole trace — the same shape as the DTLS ladder's give-up.
        log.warn('[VOIP] Giving up ICE restart after retries', connectionId, Date.now().toString().slice(-5));
        recordVoipIceRestartGiveUp();
        reportRtc('ice-restart-give-up', {
          connectionId,
          iceRestarts: this.iceRestartCounts[connectionId] ?? 0,
          maxIceRestarts: this.maxIceRestarts,
          iceConnectionState: pc.iceConnectionState,
          connectionState: pc.connectionState,
          signalingState: pc.signalingState,
          hasStream: !!connection?.stream,
          transportFailed: !!connection?.transportFailed,
        });
      }
      // Write the peer off — an exhausted ICE ladder is the dominant network-drop path, and until
      // now only the DTLS ladder ever set the flag `filterDeadTransportTiles` reads (AVS-4420).
      // Deliberately outside the reporting latch above: the write-off must not depend on whether
      // the warn fired, and `markTransportFailed` is idempotent. Locally one-way — the tile returns
      // when the remote restarts ICE or signaling replaces the peer, not on its own.
      this.markTransportFailed(connectionId);
      return;
    }
    this.iceRestartCounts[connectionId] = (this.iceRestartCounts[connectionId] ?? 0) + 1;
    recordVoipIceRestart();
    log.log('[VOIP] Restarting ICE', connectionId, Date.now().toString().slice(-5));
    reportRtc('ice-restart', {
      connectionId,
      attempt: this.iceRestartCounts[connectionId],
      maxIceRestarts: this.maxIceRestarts,
      iceConnectionState: pc.iceConnectionState,
      connectionState: pc.connectionState,
    });
    pc.restartIce();
  }

  private clearIceGraceTimer(connectionId: string): void {
    if (this.iceGraceTimers[connectionId]) {
      clearTimeout(this.iceGraceTimers[connectionId]);
      delete this.iceGraceTimers[connectionId];
    }
  }

  addMyTrackToAllPeers(track: MediaStreamTrack) {
    const remoteConnections = this.store.get('remoteConnections');
    Object.values(remoteConnections).forEach(({ peerConnection, connectionId }) => {
      if (peerConnection) {
        const needsOffer = this.sendTrackToPeer(track, peerConnection);
        if (needsOffer) {
          this.sendOffer(peerConnection, connectionId);
        }
      }
    });
  }

  removeMyTrackFromAllPeers(track: MediaStreamTrack) {
    const remoteConnections = this.store.get('remoteConnections');
    Object.values(remoteConnections).forEach(({ connectionId, peerConnection }) => {
      if (!peerConnection) {
        return;
      }
      const sender = peerConnection.getSenders()?.find((sender) => sender.track === track);

      if (sender) {
        // Use replaceTrack(null) instead of removeTrack to keep the sender/transceiver
        // reusable. This avoids orphan sender accumulation and SDP growth on repeated
        // mute/unmute cycles. Muted state is signaled separately via StreamUpdate.
        log.log('[VOIP] Muting sender for', track.kind, 'on', connectionId, Date.now().toString().slice(-5));
        sender.replaceTrack(null).catch((err: unknown) => {
          log.log('[VOIP] replaceTrack(null) failed for', connectionId, err, Date.now().toString().slice(-5));
        });
      }
    });
  }

  private async handleOfferSignals(data: RTCSessionDescriptionInit | RTCIceCandidate, from?: string) {
    const remoteConnections = this.store.get('remoteConnections');
    const connection = remoteConnections[from!];

    if (!connection?.peerConnection) {
      console.warn('[VOIP] Peer Connection not found for offer from', from);
      return;
    }

    const peerConnection = connection.peerConnection;
    const signalingState = peerConnection.signalingState;

    // AVS-4302 — perfect-negotiation glare resolution. Two publishers (two dealer masters) can
    // fire offers simultaneously; the old code only handled 'stable' and silently dropped a
    // colliding offer received in have-local-offer, deadlocking the master↔master pair.
    //
    // have-remote-offer: we are already applying/answering a prior offer. A second one is a race
    // artifact — setRemoteDescription(offer) does NOT implicit-rollback cleanly from this state —
    // so drop it; the peer re-offers from stable if it still needs to.
    if (signalingState === 'have-remote-offer') {
      log.log('[VOIP] Ignoring offer while answering a prior one (have-remote-offer)', from);
      return;
    }

    // Empty cid (pre-connect / mid-reconnect window): '' < anything would make BOTH peers
    // classify as polite (double rollback → dropped answers → deadlock), so never role-classify
    // here. Answer only from a clean stable state; otherwise drop and let the peer re-offer.
    const myConnectionId = this.store.get('me').connectionId;
    if (!myConnectionId) {
      if (signalingState === 'stable') {
        await this.answerOffer(peerConnection, data as RTCSessionDescriptionInit, from!);
      } else {
        log.log('[VOIP] Offer received with unknown local cid in non-stable state; ignoring', from);
      }
      return;
    }

    // Only have-local-offer (or a still-in-flight local offer) is a rollback-able collision. The
    // impolite peer (higher cid — the same one getInitiator picks) keeps its own offer and drops
    // the inbound one; the polite peer rolls back and answers.
    const polite = this.isPolite(myConnectionId, from!);
    const rollbackCollision = this.makingOffer[from!] || signalingState === 'have-local-offer';
    if (!polite && rollbackCollision) {
      log.log('[VOIP] Ignoring colliding offer (impolite peer keeps its own)', from);
      this.ignoringOffer[from!] = true;
      return;
    }

    await this.answerOffer(peerConnection, data as RTCSessionDescriptionInit, from!);
  }

  /**
   * AVS-4302: accept an offer and reply with an answer. `setRemoteDescription(offer)` performs
   * an IMPLICIT ROLLBACK of our own pending offer when called in have-local-offer (the polite
   * path); from stable it is a plain apply. Rollback discards only the pending local SDP, NOT our
   * RTCRtpSenders/transceivers — so a publishing master's camera senders survive and createAnswer
   * re-advertises them sendrecv (do NOT re-add tracks here; that would double-add).
   */
  private async answerOffer(
    peerConnection: RTCPeerConnection,
    offer: RTCSessionDescriptionInit,
    from: string
  ): Promise<void> {
    log.log('[VOIP] Accepting offer from ', from, peerConnection.signalingState, Date.now().toString().slice(-5));

    await peerConnection.setRemoteDescription(offer);
    reportRtc('sdp', { connectionId: from, direction: 'remote', description: offer });
    // We accepted an offer — the surviving negotiation is this one; stop swallowing its ICE.
    this.ignoringOffer[from] = false;

    log.log('[VOIP] Remote Description is SET for', from, Date.now().toString().slice(-5));
    const answer = await peerConnection.createAnswer();

    log.log('[VOIP] Answer created', from, answer, Date.now().toString().slice(-5));

    await peerConnection.setLocalDescription(answer);
    reportRtc('sdp', { connectionId: from, direction: 'local', description: answer });

    log.log('[VOIP] Local Description is SET for', from, answer, Date.now().toString().slice(-5));

    this.signalerClient.signal({
      action: ERTCEvents.PeerSignal,
      to: [from],
      data: answer,
    });

    log.log('[VOIP] Answer sent to', from, Date.now().toString().slice(-5));
  }

  private shareMyStream(): void {
    log.log('[VOIP] Sharing my stream', Date.now().toString().slice(-5));
    const { me, remoteConnections } = this.store.data;

    Object.values(remoteConnections).forEach((remoteConnection) => {
      if (me.stream) {
        me.stream.getTracks().forEach((track: MediaStreamTrack) => {
          log.log(
            '[VOIP] Sharing my stream track with ',
            remoteConnection.connectionId,
            Date.now().toString().slice(-5)
          );

          const senders = remoteConnection.peerConnection?.getSenders();
          const senderExists = senders?.some((sender) => sender.track === track);
          if (!senderExists) {
            this.prepareConnectionAndOffer(remoteConnection.connectionId);
          } else {
            log.log(
              '[VOIP] Sender already exists for ',
              remoteConnection.connectionId,
              Date.now().toString().slice(-5)
            );
          }
        });
      }
    });

    this.registerMyVideoStreamListeners();
  }

  /**
   * `new RTCPeerConnection`, and nothing else, inside the try.
   *
   * Reported once per client rather than once per peer: the refusal is a property of the browser,
   * not of the peer being admitted, and the offer burst in a multi-party call would otherwise
   * produce one alert per participant for a single cause.
   */
  private constructPeerConnection(connectionId: string): RTCPeerConnection | undefined {
    try {
      return new RTCPeerConnection({ iceServers: this.iceServers });
    } catch (error) {
      this.reportPeerConnectionUnavailable(connectionId, error);
      return undefined;
    }
  }

  private reportPeerConnectionUnavailable(connectionId: string, error: unknown): void {
    const name = error instanceof Error ? error.name : typeof error;
    const message = error instanceof Error ? error.message : String(error);

    log.error('[VOIP] This browser refused to construct a peer connection', connectionId, name, message);

    if (this.peerConnectionUnavailableReported) {
      return;
    }

    this.peerConnectionUnavailableReported = true;

    reportRtc('peer-connection-unavailable', {
      connectionId,
      name,
      message,
      // The three things that distinguish the causes: a page that is framed is probably missing an
      // `allow` attribute, a non-native constructor means something replaced it, and a secure
      // context is a precondition for WebRTC at all. `envTap` captures the full picture; these are
      // here so the `rtc` lane is readable on its own.
      framed: typeof window !== 'undefined' && window.self !== window.top,
      nativeConstructor: typeof RTCPeerConnection === 'function' && `${RTCPeerConnection}`.includes('[native code]'),
      secureContext: typeof window !== 'undefined' && window.isSecureContext,
    });

    // The toast belongs to `utils/ui/rtc.ts`, which is where every other VOIP notification lives.
    // Emitting rather than importing `state/toast` also keeps this module's graph closed: it is
    // deliberately narrow (see the sink comment above), and pulling the toast machinery in here
    // drags `components/Toast` and `utils/alerts` into the lambda and vitest graphs with it.
    this.emit('peer:unavailable', connectionId, { name, message });
  }

  /**
   * Build the peer connection for `connectionId`, or `undefined` if this browser will not build one.
   *
   * ## Why this can return nothing
   *
   * Session `312d2e01-…` (2026-09-05): a customer's browser threw
   * `NotAllowedError: Failed to construct 'RTCPeerConnection': RTCPeerConnection is not allowed` —
   * a Permissions-Policy refusal, which an embedding iframe without `allow="camera; microphone"`,
   * an enterprise policy or a WebRTC-blocking extension all produce. Signaling was healthy, the
   * dealer's offer arrived, every ICE candidate arrived. There was simply nothing to put them in.
   *
   * Before this guard the throw escaped `admitPeerFromOffer` as an unhandled rejection, and every
   * subsequent candidate logged `[VOIP] Peer Connection not found` — thirteen times in that session,
   * with no error state, no toast and no retry. The dealer sat in `have-local-offer` until
   * `convergence-failed` eight seconds later. The customer was shown nothing at all.
   *
   * Retrying is pointless: this is a capability refusal, not a transient fault. So the failure is
   * recorded once at `alert` priority (it is worth waking someone), the user is told once (the
   * toast dedups on `notificationId`), and callers get `undefined` and skip the peer.
   */
  private createPeerConnection(connectionId: string): RTCPeerConnection | undefined {
    const { remoteConnections } = this.store.data;
    const remoteConnection = remoteConnections[connectionId];
    // AVS-4391: the refresh loop normally keeps this list live. If it has been failing (the
    // /ice-servers endpoint is down), we still have to build the peer — there is no other source
    // of credentials — but say so, because the symptom otherwise is a silent gray tile with
    // nothing in the log tying it to dead TURN credentials.
    if (this.iceServersExpireAt !== null && Date.now() >= this.iceServersExpireAt) {
      log.warn(
        '[VOIP] Building peer connection with EXPIRED TURN credentials; expect no relay candidates',
        connectionId,
        Date.now().toString().slice(-5)
      );
    }
    const peerConnection = this.constructPeerConnection(connectionId);

    if (!peerConnection) {
      return undefined;
    }

    log.log('[VOIP] Creating Peer Connection - ', connectionId, Date.now().toString().slice(-5));

    // AVS-4430: the ONLY RTCPeerConnection construction site in the app, so this is the one place a
    // recorder can see every peer — including the ones that never reach `window.voipRtcConnection`
    // because their connect FAILED. `iceServerUrls` says which STUN/TURN servers this pc was even
    // allowed to try; the credentials that ride alongside them are deliberately not passed.
    reportRtc('pc-created', {
      connectionId,
      iceServerUrls: this.iceServers.flatMap((server) =>
        typeof server.urls === 'string' ? [server.urls] : (server.urls ?? [])
      ),
      iceCredentialsExpired: this.iceServersExpireAt !== null && Date.now() >= this.iceServersExpireAt,
      isRebuild: !!remoteConnection?.peerConnection,
    });

    /** Event listeners for the RTC Connection */

    // Handle ICE candidate generation
    peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        reportRtc('ice-candidate', { connectionId, direction: 'local', candidate: event.candidate });
        // Send the generated ICE candidate to the remote peer via the signaling server
        this.signalerClient.signal({
          action: ERTCEvents.PeerSignal,
          to: [connectionId],
          data: event.candidate,
        });
      } else {
        // A null candidate is end-of-gathering. Silent today, and it is the difference between
        // "we gathered nothing" and "we are still gathering".
        reportRtc('ice-gathering-complete', {
          connectionId,
          iceGatheringState: peerConnection.iceGatheringState,
        });
      }
    };

    // Handle incoming media streams from the remote peer
    peerConnection.ontrack = (event: RTCTrackEvent) => {
      const stream = event.streams[0];
      const track = event.track;
      log.log(`[VOIP] received a track from peer...`, connectionId, track);
      const remoteConnections = this.store.get('remoteConnections');
      const remoteConnection = remoteConnections[connectionId];
      if (remoteConnection) {
        // Create a new MediaStream with only live tracks to force React re-render.
        // Chrome requires srcObject reassignment to pick up new tracks added to a stream
        // after removeTrack+addTrack renegotiation (same stream ref = no React update).
        // When a track arrives via replaceTrack renegotiation, event.streams[0] may be undefined
        // (SDP has msid:-). Fall back to combining existing stream tracks with the new track.
        let sourceTracks: MediaStreamTrack[];
        if (stream) {
          sourceTracks = stream.getTracks();
        } else if (remoteConnection.stream) {
          sourceTracks = [...remoteConnection.stream.getTracks().filter((t) => t.id !== track.id), track];
        } else {
          sourceTracks = [track];
        }
        const activeStream = new MediaStream(sourceTracks.filter((t) => t.readyState !== 'ended'));
        // Standby suppression: disable tracks BEFORE the store write so stream containers
        // React already holds (same track objects) go silent in the same tick.
        this.applyIncomingMediaPolicy(activeStream);
        log.log(
          `[VOIP] ontrack: creating new MediaStream with`,
          activeStream.getTracks().length,
          'live tracks for',
          connectionId
        );
        this.store.setValues({
          remoteConnections: {
            ...remoteConnections,
            [connectionId]: {
              ...remoteConnection,
              stream: activeStream,
            },
          },
        });

        // AVS-4430 media-health: the moment a stream-less remote stops being stream-less. Paired
        // with the `peer-added` record, this bounds how long a tile was gray — the strong
        // transmission-failure signal, and free to read.
        reportRtc('stream-attached', {
          connectionId,
          trackKind: track.kind,
          trackId: track.id,
          trackMuted: track.muted,
          trackEnabled: track.enabled,
          trackReadyState: track.readyState,
          streamTracks: activeStream.getTracks().length,
          hadStream: !!remoteConnection.stream,
        });

        // Add stream to speaker detector when it's an audio track
        if (track.kind === 'audio' && activeStream) {
          this.speakerDetector.addStream(connectionId, activeStream);
        }
      }
    };

    // AVS-4302: complete perfect-negotiation. onnegotiationneeded is the single offer engine for
    // RENEGOTIATION (a track/direction change, or restartIce) — driveNegotiation offers only from a
    // clean stable state with the makingOffer guard, so it never collides with an in-flight offer.
    // Initial offers stay explicit + getInitiator-gated in prepareConnectionAndOffer (glare-free
    // common case). The signaling/ICE hooks drive recovery: stable clears the awaiting-answer timer;
    // ICE failure escalates to restartIce.
    //
    // AVS-4430: each of the four transitions is reported BEFORE its handler runs, so a recording
    // shows the state the app reacted to rather than the state it left behind.
    peerConnection.onnegotiationneeded = () => {
      reportRtc('negotiation-needed', { connectionId, signalingState: peerConnection.signalingState });
      void this.driveNegotiation(connectionId);
    };
    peerConnection.onsignalingstatechange = () => {
      reportRtc('signaling-state', { connectionId, signalingState: peerConnection.signalingState });
      this.handleSignalingStateChange(connectionId);
    };
    peerConnection.oniceconnectionstatechange = () => {
      reportRtc('ice-state', {
        connectionId,
        iceConnectionState: peerConnection.iceConnectionState,
        connectionState: peerConnection.connectionState,
        iceRestarts: this.iceRestartCounts[connectionId] ?? 0,
      });
      this.handleIceConnectionStateChange(connectionId);
    };

    // Close → remove; DTLS-failed → rebuild (see handleConnectionStateChange).
    peerConnection.onconnectionstatechange = () => {
      reportRtc('connection-state', {
        connectionId,
        connectionState: peerConnection.connectionState,
        iceConnectionState: peerConnection.iceConnectionState,
        transportRebuilds: this.transportRebuilds[connectionId] ?? 0,
      });
      this.handleConnectionStateChange(connectionId);
    };

    // Update the state to associate the peer connection with the remote connection.
    log.log(
      '[VOIP] Peer Connection Created - ',
      connectionId,
      'current remote connections',
      remoteConnections,
      Date.now()
    );
    this.store.setValues({
      remoteConnections: {
        ...remoteConnections,
        [connectionId]: {
          ...remoteConnection,
          peerConnection,
          // AVS-4392: this entry just got a genuinely fresh transport, so any previous give-up no
          // longer describes it. Defensive today — a flagged peer is turned away at the top of
          // rebuildPeerTransport, so nothing currently reaches here for one — but it keeps the
          // flag tied to the transport it describes rather than to the entry that outlives it.
          transportFailed: false,
        },
      },
    });

    return peerConnection;
  }

  private async prepareConnectionAndOffer(connectionId: string, receiveOnly = false): Promise<void> {
    const { me, remoteConnections } = this.store.data;

    if (remoteConnections) {
      // Create a new RTCPeerConnection with the specified ICE servers
      const peerConnection = remoteConnections[connectionId]?.peerConnection || this.createPeerConnection(connectionId);

      if (!peerConnection) {
        return;
      }

      // Add each track from the user's stream to the peer connection if available.
      // AVS-4302: `receiveOnly` forces the recvonly path even when a local stream exists, so
      // a standby (negotiating purely to RECEIVE the master's loopback) never adds tracks and
      // can never publish a second dealer stream — even if a camera-grab race left it a local
      // stream. The offer below is still sent when we are the lexical initiator.
      if (me.stream && !receiveOnly) {
        log.log('[VOIP] Sharing my stream to', connectionId, Date.now().toString().slice(-5));
        this.startStreamingMe(peerConnection);
        this.signalMutedState(connectionId);
      } else {
        // No local stream (or receive-only) - receive-only mode
        log.log('[VOIP] No outgoing stream - receive-only mode for', connectionId, Date.now().toString().slice(-5));
      }

      const initiator = this.getInitiator(me.connectionId, connectionId);
      const iAmTheInitiator = initiator === me.connectionId;
      log.log('[VOIP] Initiator is ', initiator, Date.now().toString().slice(-5));

      if (iAmTheInitiator) {
        // Same guard as driveNegotiation: a re-publish/demotion pass can hit a pc that is
        // mid-handshake; offering there throws InvalidStateError as an unhandled rejection.
        // onnegotiationneeded/forceReoffer pick up whatever this skip leaves undone.
        if (peerConnection.signalingState === 'stable' && !this.makingOffer[connectionId]) {
          this.sendOffer(peerConnection, connectionId).catch((err: unknown) => {
            log.log('[VOIP] Initial offer failed for', connectionId, err, Date.now().toString().slice(-5));
          });
        } else {
          log.log('[VOIP] Skipping offer (pc not stable / offer in flight)', connectionId);
        }
      } else {
        log.log('[VOIP] Not creating offer for ', connectionId, me, Date.now().toString().slice(-5));
        // Never-negotiated pair: the initiator owes us the first offer — arm the waiting-side
        // deadline in case it never arrives. A live pair (promotion/demotion re-pass) needs no
        // watchdog: renegotiation there is event-driven via onnegotiationneeded.
        if (!peerConnection.remoteDescription) {
          this.armWaitingOfferWatchdog(connectionId);
        }
      }

      this.armConvergenceReport(connectionId);
    }
  }
  private getInitiator = (a: string, b: string) => {
    return a > b ? a : b;
  };

  // AVS-4302: perfect-negotiation role. The impolite peer (higher connectionId — the same one
  // getInitiator picks) keeps its offer on a collision; the polite peer (lower) rolls back and
  // answers. Exactly one of any pair is polite. Returns false when our cid is unknown so we never
  // guess a role (handleOfferSignals handles the empty-cid case explicitly before calling this).
  private isPolite = (myConnectionId: string, peerConnectionId: string) =>
    !!myConnectionId && myConnectionId < peerConnectionId;

  private removePeer = (connectionId: string) => {
    const remoteConnections = this.store.get('remoteConnections');
    const departing = remoteConnections[connectionId];
    reportRtc('peer-removed', {
      connectionId,
      hasStream: !!departing?.stream,
      transportFailed: !!departing?.transportFailed,
      metaPending: !!departing?.metaPending,
      connectionState: departing?.peerConnection?.connectionState ?? '',
      iceConnectionState: departing?.peerConnection?.iceConnectionState ?? '',
      remaining: Object.keys(remoteConnections).length - 1,
    });
    const filteredRemoteConnections = Object.keys(remoteConnections).reduce(
      (acc: Record<string, TRTCConnection>, key: string) => {
        if (key !== connectionId) {
          acc[key] = remoteConnections[key];
        }
        return acc;
      },
      {}
    );
    this.store.setValues({ remoteConnections: filteredRemoteConnections });

    // AVS-4302: drop this peer's glare-resolution flags so a reused connectionId can't inherit a
    // stale role/ignore state. Cancel its recovery timers and delete its retry/restart budgets too,
    // so a takeover that re-adds the SAME connectionId is not born with an exhausted budget.
    delete this.makingOffer[connectionId];
    delete this.ignoringOffer[connectionId];
    if (this.renegotiationTimers[connectionId]) {
      clearTimeout(this.renegotiationTimers[connectionId]);
      delete this.renegotiationTimers[connectionId];
    }
    this.clearIceGraceTimer(connectionId);
    this.clearConvergenceTimer(connectionId);
    delete this.renegotiationRetries[connectionId];
    delete this.iceRestartCounts[connectionId];
    delete this.iceGiveUpReported[connectionId];
    delete this.transportRebuilds[connectionId];
    delete this.silentTransportEscalations[connectionId];
    // AVS-4392: the candidate queue was the one per-peer map this cleanup missed. Harmless while
    // candidates were only ever queued pre-first-negotiation; with restart candidates now retained
    // for retry, a peer departing mid-restart would otherwise leave its queue in the map for the
    // rest of the session, re-walked forever by the no-arg flush.
    delete this.pendingCandidates[connectionId];

    this.speakerDetector.removeStream(connectionId);
    // If no more connections, stop the detector
    if (Object.keys(filteredRemoteConnections).length === 0) {
      this.speakerDetector.stop();
    }

    log.log(
      '[VOIP] Connection Removed - ',
      connectionId,
      this.store.data.remoteConnections,
      Date.now().toString().slice(-5)
    );
  };

  // AVS-4278: derive the signaled muted state from the user's choice AND the actual outgoing
  // tracks — an unset flag must never mute a live call, and a track-less kind must never be
  // signaled audible.
  private signalMutedState = (connectionId: string) => {
    const localCamera = myCameraStream();
    const hasLiveAudio = !!localCamera.stream?.getAudioTracks().some((track) => track.readyState === 'live');
    const hasLiveVideo = !!localCamera.stream?.getVideoTracks().some((track) => track.readyState === 'live');
    this.signalerClient.signal({
      action: ERTCEvents.StreamUpdate,
      to: [connectionId],
      data: {
        audioMuted: localCamera.get('isAudioEnabled') === false || !hasLiveAudio,
        videoMuted: localCamera.get('isVideoEnabled') === false || !hasLiveVideo,
      },
    });
  };

  private signalMutedStateToAllPeers = () => {
    Object.values(this.store.get('remoteConnections')).forEach(({ connectionId }) => {
      this.signalMutedState(connectionId);
    });
  };

  public toggleMedia = (type: 'audio' | 'video'): VoipMediaState | undefined => {
    const { me } = this.store.data;
    // Cannot toggle media if no local stream exists (receive-only mode)
    if (!me.stream) {
      log.warn('[VOIP] Cannot toggle media - no local stream (receive-only mode)');
      return;
    }

    let microphoneEnabled = myCameraStream().get('isAudioEnabled');
    let cameraEnabled = myCameraStream().get('isVideoEnabled');

    switch (type) {
      case 'audio':
        microphoneEnabled = myCameraStream().toggleAudio();
        break;
      case 'video':
        cameraEnabled = myCameraStream().toggleVideo();
        break;
      default:
        break;
    }
    /** Signal other peers about the stream change */
    this.signalMutedStateToAllPeers();

    return { microphoneEnabled, cameraEnabled };
  };
}
